/**
 * Support Session Constants
 *
 * Constants for managing support account sessions that allow
 * admin users to temporarily access organisations.
 */

export const SUPPORT_SESSION_STATUS = {
    /** Request submitted, awaiting approval from org */
    PENDING: 'pending',
    /** Request approved, support account has access */
    APPROVED: 'approved',
    /** Request rejected by org */
    REJECTED: 'rejected',
    /** Session has expired (automatic) */
    EXPIRED: 'expired',
    /** Session manually revoked by org */
    REVOKED: 'revoked',
} as const;

export const SUPPORT_SESSION_SOURCE = {
    /** Session requested through the admin console and approved by the tenant */
    ADMIN_REQUEST: 'admin_request',
    /** Fixed-duration session provisioned by trusted organisation-onboarding code */
    ONBOARDING: 'onboarding',
} as const;

export type SupportSessionSource = (typeof SUPPORT_SESSION_SOURCE)[keyof typeof SUPPORT_SESSION_SOURCE];

export type SupportSessionStatus = (typeof SUPPORT_SESSION_STATUS)[keyof typeof SUPPORT_SESSION_STATUS];

export const SUPPORT_SESSION_STATUS_LABELS: Record<SupportSessionStatus, string> = {
    pending: 'Pending Approval',
    approved: 'Active',
    rejected: 'Rejected',
    expired: 'Expired',
    revoked: 'Revoked',
} as const;

/**
 * Statuses that allow approval (only pending)
 */
export const APPROVABLE_SUPPORT_SESSION_STATUSES: SupportSessionStatus[] = [SUPPORT_SESSION_STATUS.PENDING] as const;

/**
 * Statuses that allow revocation (only approved)
 */
export const REVOCABLE_SUPPORT_SESSION_STATUSES: SupportSessionStatus[] = [SUPPORT_SESSION_STATUS.APPROVED] as const;

/**
 * Default session duration in days when creating a new support session request
 */
export const DEFAULT_SUPPORT_SESSION_DURATION_DAYS = 7;

/**
 * Maximum allowed duration for tenant-approved support-session requests.
 */
export const MAX_SUPPORT_SESSION_DURATION_DAYS = 90;

/**
 * Fixed duration for the trusted, auto-approved onboarding support session.
 *
 * This explicit product exception does not pass through the request/approval APIs and remains
 * tenant-visible and revocable. Requested support sessions are still capped by
 * MAX_SUPPORT_SESSION_DURATION_DAYS.
 */
export const ONBOARDING_SUPPORT_SESSION_DURATION_DAYS = 183;

/**
 * How long an organisation has to approve a support session request (CUR-2328), counted from the
 * request. The same as the default session length: a default request lapses when the access it asks
 * for would have ended anyway, and a longer request (up to `MAX_SUPPORT_SESSION_DURATION_DAYS`)
 * cannot wait weeks for an approval nobody remembers asking for.
 */
export const SUPPORT_SESSION_REQUEST_LIFETIME_DAYS = DEFAULT_SUPPORT_SESSION_DURATION_DAYS;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The last moment a pending request can be approved: its lifetime from the request, or the end of
 * the access it asks for if that comes first. At and after it the request has lapsed; approval
 * refuses it and the `expire-support-sessions` sweep marks it expired.
 */
export function supportSessionApprovalDeadline(session: { createdAt: Date; expiresAt: Date }): Date {
    const lifetimeEnd = new Date(session.createdAt.getTime() + SUPPORT_SESSION_REQUEST_LIFETIME_DAYS * DAY_MS);
    return lifetimeEnd < session.expiresAt ? lifetimeEnd : session.expiresAt;
}

/**
 * The earliest `createdAt` a request still inside its lifetime can have at `now`: a pending request
 * created at or before it has lapsed.
 */
export function supportSessionRequestCreatedCutoff(now: Date): Date {
    return new Date(now.getTime() - SUPPORT_SESSION_REQUEST_LIFETIME_DAYS * DAY_MS);
}

/**
 * PostHog flag (off by default) for emailing an organisation's approvers when a support session is
 * requested (CUR-2328). It also keeps the new `SUPPORT_SESSION_REQUESTED` outbox kind unwritten until
 * every running worker knows it (docs/plan/effect-migration/job-email-outbox.md, order of work).
 */
export const SUPPORT_SESSION_REQUEST_EMAIL_FLAG = 'enable-support-session-request-email';
